'use client';

import React, { useMemo, useEffect, useState } from 'react';
import { Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import PropTypes from 'prop-types';
import ClinicMapMarker from './ClinicMapMarker';
import MapPopup from '../Shared/MapPopup';

const DEFAULT_MAP_OPTIONS = {
    zoom: 8,
    center: {
        lat: 51.5048541,
        lng: -0.1136621
    },
    options: {
        disableDefaultUI: true,
        clickableIcons: false,
        zoomControl: true,
    },
    mapId: 'citydoc-find-clinic',
};

ClinicMap.propTypes = {
    selectedAddress: PropTypes.shape({
        address: PropTypes.string,
        lat: PropTypes.number,
        lng: PropTypes.number,
    }),
    results: PropTypes.array.isRequired,
    curTab: PropTypes.string,
    fitMarkers: PropTypes.bool,
};

export default function ClinicMap({ selectedAddress, results, curTab, fitMarkers }) {

    let mapDefaultOptions = DEFAULT_MAP_OPTIONS;

    // center map on selected address if available
    if (selectedAddress && Object.keys(selectedAddress).length) {
        mapDefaultOptions.center = {
            lat: selectedAddress.lat,
            lng: selectedAddress.lng
        };
    }

    const map = useMap();
    const mapOptions = useMemo(() => mapDefaultOptions.options, []);
    const [infowindowOpen, setInfowindowOpen] = useState(false);
    const [activeMarker, setActiveInfoMarker] = useState(null);
    const [activeResult, setActiveResult] = useState(null);

    const onMarkerSelect = (marker, result) => {
        if (activeMarker && activeMarker === marker) {
            onCloseMarker();
            return;
        }
        setInfowindowOpen(true);
        setActiveInfoMarker(marker);
        setActiveResult(result);
    };

    const onCloseMarker = () => {
        setInfowindowOpen(false);
        setActiveInfoMarker(null);
        setActiveResult(null);
    };

    // pan to an address when changed
    useEffect(() => {
        if (!map) return;
        if (selectedAddress?.lat && selectedAddress?.lng) {
            map.panTo({
                lat: selectedAddress.lat,
                lng: selectedAddress.lng,
            });
            map.setZoom(DEFAULT_MAP_OPTIONS.zoom);
        }
    }, [map, selectedAddress]);

    // fit markers when changed
    useEffect(() => {
        if (!map || !fitMarkers) return;
        const bounds = new window.google.maps.LatLngBounds();
        results.map((result) => {
            bounds.extend({
                lat: result.lat,
                lng: result.lng,
            });
        });
        map.fitBounds(bounds);

    }, [map, results]);

    return (
        <>
            <div className="clinic-finder__result-count">{results.length}</div>
            <div className={` ${curTab == 'map' ? '' : 'u-hidden'}`}>
                <Map
                    defaultZoom={mapDefaultOptions.zoom}
                    defaultCenter={mapDefaultOptions.center}
                    className="clinic-finder__map"
                    options={mapOptions}
                    mapId={mapDefaultOptions.mapId}
                    onClick={onCloseMarker}
                >
                    {selectedAddress && Object.keys(selectedAddress).length && (
                        <AdvancedMarker
                            position={{
                                lat: selectedAddress.lat,
                                lng: selectedAddress.lng,
                            }}
                        />
                    )}
                    {results.map((result) => (
                        <ClinicMapMarker
                            key={result.id}
                            data={result}
                            onMarkerSelect={onMarkerSelect}
                        />
                    ))}
                    {infowindowOpen && activeResult && (
                        <MapPopup
                            activeMarker={activeMarker}
                            activeResult={activeResult}
                            onClosePopup={onCloseMarker}
                            selectText="Book this clinic"
                            showDirections={true}
                            showClinicLink={true}
                        />
                    )}
                </Map>
            </div>
        </>
    );
}
